// HomeWithAssistant.js
import React, { useEffect, useRef, useState } from "react";

/**
 * HomeWithAssistant
 *
 * - Clicking the floating assistant button:
 *   - First click: greets -> asks "How are you?" -> waits user answer -> waits 2s -> responds and asks which section
 *   - Next clicks: directly asks "Now which section do you want to move?"
 * - When a section command is recognized (about, services, features, hero, blog, contact),
 *   the page scrolls to that section, assistant says "Here is your <section> section. For next command, click again."
 *   and assistant reads+highlights the section text.
 * - A "Read Full Section" button in About reads/highlights and toggles Stop Reading.
 *
 * Add this component where you want it. Ensure your page has elements with ids:
 *  - hero, about (with .about-description), services, features, blog, contact
 *
 * Notes on cross-browser:
 * - Voice selection tries to pick a female-sounding voice by common voice names and words ("female" etc).
 * - If onboundary isn't supported, fallback estimates word timing.
 */

const HomeWithAssistant = () => {
  const [isReading, setIsReading] = useState(false);
  const [assistantActive, setAssistantActive] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [voicesReady, setVoicesReady] = useState(false);

  const speechRecRef = useRef(null);
  const highlightTimerRef = useRef(null);
  const fallbackTimerRef = useRef(null);
  const utteranceRef = useRef(null);
  const selectedVoiceRef = useRef(null);

  // Preselected female voice name preferences (order matters)
  const femaleVoicePriority = [
    // common voice names on Chrome/Windows/Mac OS variants
    "Google UK English Female",
    "Google US English",
    "Samantha", // macOS
    "Alex", // macOS (male but good TTS); fallback
    "Microsoft Zira Desktop",
    "Microsoft Hazel Desktop",
    "Microsoft Heera Desktop",
    "Karen",
  ];

  useEffect(() => {
    // Load voices and choose a preferred voice when available
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices() || [];
      if (voices.length > 0) {
        selectedVoiceRef.current = choosePreferredVoice(voices);
        setVoicesReady(true);
      }
    };

    loadVoices();
    // Some browsers load voices asynchronously
    window.speechSynthesis.onvoiceschanged = () => {
      loadVoices();
    };

    // cleanup
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
      stopReadingImmediate();
      if (speechRecRef.current) {
        try {
          speechRecRef.current.stop();
        } catch (e) {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const choosePreferredVoice = (voices) => {
    // try by priority names
    for (const name of femaleVoicePriority) {
      const found = voices.find((v) => v.name && v.name.includes(name));
      if (found) return found;
    }

    // otherwise prefer voices where lang includes 'en' and name includes female-like keywords
    const femaleLike = voices.find(
      (v) =>
        v.lang && v.lang.toLowerCase().startsWith("en") &&
        (v.name && /female|woman|zira|samantha|karen|hazel|alloy|google/i.test(v.name))
    );
    if (femaleLike) return femaleLike;

    // final fallback: any 'en' voice
    const en = voices.find((v) => v.lang && v.lang.toLowerCase().startsWith("en"));
    if (en) return en;

    // else the first available voice
    return voices[0] || null;
  };

  /* -------------------- Text reading + highlighting -------------------- */

  // Read a element's text content with word-by-word highlight.
  // elementSelector: CSS selector or element (if passed as element, we use directly)
  const readElementText = (elementOrSelector) => {
    const element =
      typeof elementOrSelector === "string"
        ? document.querySelector(elementOrSelector)
        : elementOrSelector;
    if (!element) {
      console.warn("Element to read not found:", elementOrSelector);
      return;
    }

    stopReadingImmediate(); // ensure previous speech stopped

    const plainText = getPlainText(element);
    if (!plainText.trim()) return;

    const u = new window.SpeechSynthesisUtterance(plainText);
    u.rate = 0.9; // slower reading
    u.pitch = 1.0;
    u.lang = "en-US";
    if (selectedVoiceRef.current) u.voice = selectedVoiceRef.current;

    utteranceRef.current = u;
    setIsReading(true);

    // bind events
    let boundarySupported = false;
    u.onboundary = (ev) => {
      boundarySupported = true;
      // event.charIndex gives index into full text
      if (ev.charIndex !== undefined) {
        applyHighlightByCharIndex(element, plainText, ev.charIndex);
      }
    };

    u.onend = () => {
      clearAllHighlighting(element, plainText);
      setIsReading(false);
      clearTimers();
    };

    // fallback if onboundary isn't supported in this browser: estimate word timings
    // We'll compute rough durations and step through words
    // Start speaking, then if onboundary didn't fire after a short delay, start fallback timer.
    window.speechSynthesis.speak(u);

    // start fallback check after 250ms
    fallbackTimerRef.current = setTimeout(() => {
      if (!boundarySupported) {
        // start estimated highlighting
        startFallbackHighlight(element, plainText, u.rate);
      }
    }, 250);
  };

  // Get plain text of element (no tags). Keep small whitespace formatting.
  const getPlainText = (el) => {
    // prefer textContent but keep spacing
    return el.textContent || el.innerText || "";
  };

  const applyHighlightByCharIndex = (element, fullText, charIndex) => {
    // We find the current word from charIndex and wrap that word in span
    // Preserve original whitespace by reconstructing innerHTML from slices.
    const before = fullText.slice(0, charIndex);
    const after = fullText.slice(charIndex);
    // extract current word
    const wordsBefore = before.split(/\s+/);
    let currentWord = wordsBefore[wordsBefore.length - 1] || "";
    // fallback if empty
    if (!currentWord) {
      // try to skip any non-word
      const trimmed = fullText.slice(charIndex).trim();
      currentWord = trimmed.split(/\s+/)[0] || "";
    }

    const start = Math.max(0, before.lastIndexOf(currentWord));
    const end = start + currentWord.length;

    // escape HTML (we'll set innerHTML to composed string)
    const esc = (s) =>
      s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

    const part1 = esc(fullText.slice(0, start));
    const partHighlight = esc(fullText.slice(start, end));
    const part2 = esc(fullText.slice(end));

    element.innerHTML =
      part1 +
      `<span class="tts-highlight" style="background:lightblue;border-radius:3px;">` +
      partHighlight +
      `</span>` +
      part2;
  };

  const clearAllHighlighting = (element, fullText) => {
    // put plain text back
    element.innerText = fullText;
    // clear timers
    clearTimers();
  };

  const clearTimers = () => {
    if (highlightTimerRef.current) {
      clearInterval(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  };

  const startFallbackHighlight = (element, fullText, rate = 1.0) => {
    // Estimate reading duration: average characters spoken per second at rate=1 ~ 13
    // (This is heuristic; adjust if needed)
    const avgCharsPerSec = 13 * rate;
    const estimatedTotalSec = Math.max(1, fullText.length / avgCharsPerSec);
    const words = fullText.split(/\s+/).filter(Boolean);
    if (words.length === 0) return;

    // compute duration proportionally per word by its char length
    const totalChars = words.reduce((s, w) => s + w.length, 0);
    const wordDurations = words.map((w) => (w.length / totalChars) * estimatedTotalSec);

    // iterate words
    let idx = 0;
    const paintWord = () => {
      if (!words[idx]) {
        // finish
        element.innerText = fullText;
        setIsReading(false);
        clearTimers();
        return;
      }
      // Rebuild string highlighting words[idx]
      const prefix = words.slice(0, idx).join(" ");
      const current = words[idx];
      const suffix = words.slice(idx + 1).join(" ");

      // careful: reconstruct with same spacing - using split/join loses multiple spaces/newlines.
      // For fallback we will approximate using single spaces.
      element.innerHTML =
        (prefix ? prefix + " " : "") +
        `<span class="tts-highlight" style="background:lightblue;border-radius:3px;">${current}</span>` +
        (suffix ? " " + suffix : "");

      const durMs = Math.max(80, Math.round(wordDurations[idx] * 1000));
      idx++;
      highlightTimerRef.current = setTimeout(paintWord, durMs);
    };

    paintWord();
  };

  const stopReadingImmediate = () => {
    // stop speech and clear highlight/timers
    window.speechSynthesis.cancel();
    utteranceRef.current = null;
    clearTimers();
    // restore any element innerText to original for any open sections we may have changed
    // (We will try to find any highlighted elements and remove highlight)
    const highlighted = document.querySelectorAll(".tts-highlight");
    highlighted.forEach((span) => {
      const parent = span.parentNode;
      if (parent) {
        // replace parent HTML with its textContent (best-effort)
        const parentFull = parent.innerText || parent.textContent || "";
        parent.innerText = parentFull;
      }
    });
    setIsReading(false);
  };

  /* -------------------- Voice assistant logic -------------------- */

  const startAssistant = () => {
    if (assistantActive) {
      // If already active, simply ask next-question prompt
      askForSectionAgain();
      return;
    }
    setAssistantActive(true);

    if (!hasGreeted) {
      // First-time flow: greet -> ask "How are you?"
      speakText("Hello! How are you today?", () => {
        waitUserResponse(handleFirstResponse);
      });
      setHasGreeted(true);
    } else {
      askForSectionAgain();
    }
  };

  const askForSectionAgain = () => {
    // After first time, we skip long greeting
    speakText("Now which section do you want to move?", () => {
      waitUserResponse(handleSectionResponse);
    });
  };

  const speakText = (text, onEnd) => {
    // If already speaking, cancel and speak new text
    window.speechSynthesis.cancel();
    const u = new window.SpeechSynthesisUtterance(text);
    u.rate = 0.95;
    u.pitch = 1.0;
    u.lang = "en-US";
    if (selectedVoiceRef.current) u.voice = selectedVoiceRef.current;
    u.onend = onEnd;
    window.speechSynthesis.speak(u);
  };

  // Wait for user's voice input and call callback(transcript)
  const waitUserResponse = (callback) => {
    if (!("SpeechRecognition" in window) && !("webkitSpeechRecognition" in window)) {
      // fallback: use prompt
      const fallback = window.prompt("Please type your response (fallback).");
      if (fallback !== null) {
        setTimeout(() => callback(fallback.toLowerCase()), 2000); // respect 2 sec wait
      } else {
        // do nothing
      }
      return;
    }

    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new Recognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = "en-US";

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript.toLowerCase();
      // wait ~2 seconds before executing next speak to match requirement
      setTimeout(() => {
        try {
          callback(transcript);
        } catch (e) {
          console.error(e);
        }
      }, 2000);
    };

    recognition.onerror = (ev) => {
      console.warn("Speech recognition error", ev);
      // try fallback prompt after 1s
      setTimeout(() => {
        const fallback = window.prompt("I couldn't hear you. Type your response (fallback):");
        if (fallback !== null) {
          setTimeout(() => callback(fallback.toLowerCase()), 2000);
        }
      }, 1000);
    };

    recognition.onend = () => {
      // don't auto-restart here
    };

    try {
      recognition.start();
      speechRecRef.current = recognition;
      // safety timeout in case browser doesn't end recognition
      setTimeout(() => {
        try {
          recognition.stop();
        } catch (e) {}
      }, 20000);
    } catch (e) {
      console.error("Recognition start failed", e);
      const fallback = window.prompt("Please type your response (fallback).");
      if (fallback !== null) {
        setTimeout(() => callback(fallback.toLowerCase()), 2000);
      }
    }
  };

  const handleFirstResponse = (transcript) => {
    // Basic positive responses
    const positive = ["fine", "i am fine", "i'm fine", "good", "great", "doing well", "doing good"];
    const isPositive = positive.some((p) => transcript.includes(p));
    if (isPositive) {
      speakText(
        "I'm glad to hear that! I am here to guide you. Which section would you like to open?",
        () => {
          waitUserResponse(handleSectionResponse);
        }
      );
    } else {
      speakText("Okay. I am here to guide you. Which section would you like to open?", () => {
        waitUserResponse(handleSectionResponse);
      });
    }
  };

  const sectionsList = ["hero", "about", "services", "features",  "blog", "contact"];

  const handleSectionResponse = (transcript) => {
    // find a section match
    const found = sectionsList.find((s) => transcript.includes(s));
    if (found) {
      // scroll and read
      openSectionAndRead(found);
    } else {
      // try variations: 'service' -> services, 'feature' -> features
      if (transcript.includes("service")) {
        openSectionAndRead("services");
        return;
      }
      if (transcript.includes("feature")) {
        openSectionAndRead("features");
        return;
      }
      // not found
      speakText("I couldn't find that section. Please say About, Services, or Features.", () => {
        waitUserResponse(handleSectionResponse);
      });
    }
  };

  const openSectionAndRead = (sectionId) => {
    // scroll into view
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // speak the short confirmation, then read the section (with highlight)
    speakText(`Here is your ${sectionId} section. For next command, click again.`, () => {
      // after the confirmation finished, give a tiny delay then read the section content
      setTimeout(() => {
        // find the text element within the section to read
        // (for about we expect .about-description; if not present, read section innerText)
        let readTarget = null;
        if (sectionId === "about") {
          readTarget = document.querySelector("#about .about-description") || document.getElementById("about");
        } else {
          // pick first paragraph inside the section or the section itself
          const s = document.getElementById(sectionId);
          readTarget = s ? s.querySelector("p, .section-text, .about-description") || s : null;
        }
        if (readTarget) {
          readElementText(readTarget);
        }
      }, 400); // small gap so the confirmation TTS doesn't overlap
    });
  };

  /* -------------------- UI Buttons -------------------- */

  // "Read Full Section" button for About
  const handleReadButton = () => {
    const aboutEl = document.querySelector("#about .about-description");
    if (!isReading) {
      readElementText(aboutEl);
    } else {
      stopReadingImmediate();
    }
  };

  // Stop assistant & reading completely (optional UI)
  const stopAssistantAndReading = () => {
    stopReadingImmediate();
    if (speechRecRef.current) {
      try {
        speechRecRef.current.stop();
      } catch (e) {}
      speechRecRef.current = null;
    }
    setAssistantActive(false);
    setHasGreeted(false);
  };

  /* -------------------- Render -------------------- */
  return (
    <div className="index-page">
      <style>{`
        /* minimal styles for the assistant and highlight */
        .tts-highlight { background: lightblue; border-radius: 3px; }
        .assistant-button { 
          position: fixed; bottom: 30px; right: 30px; z-index: 9999;
          width: 65px; height: 65px; border-radius: 50%;
          display:flex; align-items:center; justify-content:center;
          font-size: 26px; cursor:pointer; border:none; color:#fff;
        }
        .assistant-start { background:#007bff; }
        .assistant-stop { background:#dc3545; }
      `}</style>

      <header id="header" className="header d-flex align-items-center fixed-top">
        <div className="header-container container-fluid container-xl position-relative d-flex align-items-center justify-content-between">
          <a href="#hero" className="logo d-flex align-items-center me-auto me-xl-0">
            <img src="assets/logo.svg" alt="logo" style={{height:32}} />
          </a>
          <nav id="navmenu" className="navmenu">
            <ul>
              <li><a href="#hero" className="active">Home</a></li>
              <li><a href="#about">About</a></li>
              <li><a href="#services">Services</a></li>
              <li><a href="#features">Features</a></li>
              <li><a href="#blog">Blog</a></li>
              <li><a href="#contact">Contact</a></li>
            </ul>
          </nav>
          <a className="btn-getstarted" href="#contact">Get Started</a>
        </div>
      </header>

      <main className="main" style={{ paddingTop: 90 }}>
        {/* Hero */}
        <section id="hero" className="hero section" style={{ padding: "80px 0" }}>
          <div className="container">
            <h1>Hero area</h1>
            <p>Welcome to our demo hero section.</p>
          </div>
        </section>

        {/* About */}
        <section id="about" className="about section py-5" style={{ padding: "60px 0", background: "#fafafa" }}>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-xl-6">
                <span className="about-meta">WHO WE ARE</span>
                <h2 className="about-title">Leading IT Innovation</h2>
                <p className="about-description">
                  We are a premier IT company at the forefront of innovation, delivering comprehensive technology solutions that empower businesses to grow, excel, and stay ahead of the competition.
                  Our customer-centric approach, commitment to excellence, and passion for technology make us a trusted partner in every digital transformation journey.
                </p>

                <button
                  className={`btn ${isReading ? "btn-danger" : "btn-primary"} mt-4`}
                  onClick={handleReadButton}
                >
                  {isReading ? "Stop Reading" : "Read Full Section"}
                </button>
              </div>

              <div className="col-xl-6">
                {/* dummy image */}
                <div style={{ height: 200, background: "#e9ecef", borderRadius: 8 }}></div>
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section id="services" className="section py-5">
          <div className="container">
            <h2>Services</h2>
            <p>We provide many great services including development, QA, and cloud engineering.</p>
          </div>
        </section>

        {/* Features */}
        <section id="features" className="section py-5" style={{ background: "#f7f7f7" }}>
          <div className="container">
            <h2>Features</h2>
            <p>Product features and USP explanations go here.</p>
          </div>
        </section>

        {/* Blog */}
        <section id="blog" className="section py-5">
          <div className="container">
            <h2>Blog</h2>
            <p>Latest posts shown here.</p>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="section py-5" style={{ background: "#f2f2f2" }}>
          <div className="container">
            <h2>Contact</h2>
            <p>Contact us information.</p>
          </div>
        </section>

        {/* Floating Assistant Button */}
        {!assistantActive ? (
          <button
            title="Open Voice Assistant"
            className="assistant-button assistant-start"
            onClick={startAssistant}
          >
            🔊
          </button>
        ) : (
          <div style={{ position: "fixed", right: 30, bottom: 30, zIndex: 9999, display: "flex", gap: 8 }}>
            <button
              title="Ask Section"
              className="assistant-button assistant-start"
              onClick={() => {
                // next-click behavior: ask for section
                if (!hasGreeted) {
                  startAssistant();
                } else {
                  askForSectionAgain();
                }
              }}
            >
              🗣️
            </button>
            <button
              title="Stop assistant & reading"
              className="assistant-button assistant-stop"
              onClick={stopAssistantAndReading}
            >
              ⛔
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default HomeWithAssistant;
